/** @file A mesh as viewport gizmo marks: unique triangle edges and one coloured dot per vertex (screen px). */
import { viewCamera } from '@pose2d/editor-geometry/viewCamera';
import type { Camera } from '@pose2d/geometry/types';
import type { Vec2 } from '@pose2d/math/types';
import type { GizmoDrawing } from '../../panels/viewport/viewportTypes';

/** World points (2 per vertex) to screen pixels. */
function toScreen(camera: Camera, points: ArrayLike<number>): Vec2[] {
  const out: Vec2[] = [];
  for (let i = 0; i + 1 < points.length; i += 2) out.push(viewCamera.worldToScreen(camera, points[i] ?? 0, points[i + 1] ?? 0, [0, 0]));
  return out;
}

/** Wire (each triangle edge once), dots coloured by `color(v)`, then `extra` lines (marquee, brush ring). */
export function meshGizmo(camera: Camera, points: ArrayLike<number>, triangles: readonly number[], color: (v: number) => number, extra: readonly [Vec2, Vec2][] = []): GizmoDrawing {
  const px = toScreen(camera, points);
  const seen = new Set<number>();
  const lines: [Vec2, Vec2][] = [];
  for (let i = 0; i < triangles.length; i++) {
    const a = triangles[i] ?? 0, b = triangles[i % 3 === 2 ? i - 2 : i + 1] ?? 0;
    const key = Math.min(a, b) * 65536 + Math.max(a, b);
    const pa = px[a], pb = px[b];
    if (seen.has(key) || pa === undefined || pb === undefined) continue;
    seen.add(key);
    lines.push([pa, pb]);
  }
  return { handles: [], active: null, lines: [...lines, ...extra], points: px.map((at, v) => ({ at, color: color(v) })) };
}
